import { useEffect, useRef, useState } from 'react';

export interface CameraVideoCanvasProps {
  onFrame: (imageData: ImageData) => void;
  onLog?: (msg: string) => void;
  onReady?: () => void;
  width?: number;
  height?: number;
  paused?: boolean;
}

interface MediaTrackCapabilities {
  facingMode?: string[] | string | undefined;
  focusMode?: string[];
  focusDistance?: { min?: number; max?: number };
  exposureMode?: string[];
  zoom?: { min?: number; max?: number };
  width?: { min?: number; max?: number };
  height?: { min?: number; max?: number };
  torch?: boolean;
  iso?: { min?: number; max?: number };
  exposureTime?: { min?: number; max?: number };
}

interface CameraInfo {
  device: MediaDeviceInfo;
  caps?: MediaTrackCapabilities;
  facingMode?: string | undefined;
  width?: number;
  height?: number;
  score: number;
}

export const CameraVideoCanvas2 = ({
  onFrame,
  onLog,
  onReady,
  width = 800,
  height = 600,
  paused = false,
}: CameraVideoCanvasProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [hasCameraPermission, setHasCameraPermission] = useState<boolean>(false);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

  // Request camera permissions on mount (if needed)
  useEffect(() => {
    // Only prompt for permissions if not already granted
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'camera' as PermissionName })
        .then(result => {
          if (result.state === 'denied') {
            onLog?.('Camera permission denied. Please enable camera access in your browser settings.');
          } else if (result.state === 'prompt') {
            // Try to trigger permission prompt by requesting a dummy stream
            navigator.mediaDevices.getUserMedia({ video: true })
              .then(stream => {
                stream.getTracks().forEach(track => track.stop());
                onLog?.('Camera permission granted.');
                setHasCameraPermission(true);
              })
              .catch(err => {
                onLog?.('Camera permission denied or error: ' + err.message);
              });
          } else {
            setHasCameraPermission(true);
          }
        })
        .catch(() => {
          // Fallback: just try to getUserMedia (will prompt if needed)
          navigator.mediaDevices.getUserMedia({ video: true })
            .then(stream => {
              stream.getTracks().forEach(track => track.stop());
              onLog?.('Camera permission granted.');
              setHasCameraPermission(true);
            })
            .catch(err => {
              onLog?.('Camera permission denied or error: ' + err.message);
            });
        });
    } else {
      // Fallback for browsers without Permissions API
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop());
          onLog?.('Camera permission granted.');
          setHasCameraPermission(true);
        })
        .catch(err => {
          onLog?.('Camera permission denied or error: ' + err.message);
        });
    }

    return () => {
      // Cleanup on component unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      onLog?.('Component unmounting - camera stream cleaned up');
    };
    // eslint-disable-next-line
  }, []);

  // Enumerate cameras
  useEffect(() => {
    if (!hasCameraPermission) return;

    navigator.mediaDevices.enumerateDevices()
      .then(async devices => {
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        // Log capabilities for each camera and collect info
        const cameraInfos: {
          device: MediaDeviceInfo,
          caps?: MediaTrackCapabilities,
          facingMode?: string | undefined,
          width?: number,
          height?: number,
          score: number,
        }[] = [];
        for (const cam of videoInputs) {
          let caps: MediaTrackCapabilities | undefined = undefined;
          let score: number = 0;
          let facingMode: string | string[] | undefined = undefined;
          let width: number | undefined = undefined;
          let height: number | undefined = undefined;
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { exact: cam.deviceId } },
            });
            const track = stream.getVideoTracks()[0];
            caps = track.getCapabilities?.();
            facingMode = Array.isArray(caps?.facingMode) ? caps?.facingMode?.[0] : caps?.facingMode;
            width = caps?.width?.max;
            height = caps?.height?.max;
            score = scoreCamera({ device: cam, caps, facingMode, width, height, score });
            onLog?.(`Camera "${cam.label || cam.kind || cam.deviceId}" Score "${score}" capabilities: ${caps ? JSON.stringify(caps, null, 2) : 'not available'}`);
            stream.getTracks().forEach(t => t.stop());
          } catch (e: unknown) {
            onLog?.(`Could not get capabilities for camera "${cam.label || cam.deviceId}": ${(e as Error).message}`);
          }
          cameraInfos.push({ device: cam, caps, facingMode, width, height, score });
        }
        onLog?.(`Available cameras: ${videoInputs.map(c => c.label).join(', ')}`);
        // Select best camera: prefer rear-facing, then highest resolution, else first
        const best = cameraInfos.reduce((a, b) => a.score > b.score ? a : b, cameraInfos[0]);
        // Prefer rear-facing
        if (best) {
          setSelectedCamera(best.device.deviceId);
          onLog?.(`Best camera selected: ${best.device.label || best.device.deviceId}`);
        }
      })
      .catch(e => onLog?.(`Camera enumeration error: ${e.message}`));
    // eslint-disable-next-line
  }, [hasCameraPermission]);

  // Helper to score a camera based on requirements
  const scoreCamera = (info: CameraInfo) => {
    const caps = info.caps || {};
    let score = 0;
    // 1. Facing mode
    if (Array.isArray(caps.facingMode) && caps.facingMode.includes('environment')) {
      score += 100;
    }

    // 2. Focus mode
    if (Array.isArray(caps.focusMode)) {
      if (caps.focusMode.includes('continuous')) score += 50;
      else if (caps.focusMode.includes('single-shot')) score += 25;
      else if (caps.focusMode.includes('manual')) score += 10;
    }

    // 3. Strong preference for close focus (min focus distance)
    if (caps.focusDistance?.min != null) {
      const fdMin = caps.focusDistance.min;
      score += normalize(1 / fdMin, 1, 1 / 0.02, 0, 50); // closer = better
    }

    // 4. Exposure mode
    if (Array.isArray(caps.exposureMode)) {
      if (caps.exposureMode.includes('continuous')) score += 10;
      else if (caps.exposureMode.includes('manual')) score += 5;
    }

    // 5. Zoom
    if (caps.zoom?.max != null) {
      score += normalize(caps.zoom.max, 1, 10, 0, 15);
    }

    // 6. Resolution
    if (caps.width?.max != null && caps.height?.max != null) {
      const resolution = caps.width.max * caps.height.max;
      score += normalize(resolution, 640 * 480, 4032 * 3024, 0, 50);
    }

    // 7. Torch
    if (caps.torch === true) score += 10;

    // 8. ISO
    if (caps.iso?.max != null) {
      score += normalize(caps.iso.max, 100, 12800, 0, 10);
    }

    // 9. Exposure time (minor weight)
    if (caps.exposureTime?.max != null) {
      score += normalize(caps.exposureTime.max, 0.5, 600000, 0, 5);
    }

    return score;
  };

  const normalize = (value: number, min: number, max: number, outMin: number, outMax: number) => {
    return Math.min(Math.max(((value - min) / (max - min)) * (outMax - outMin) + outMin, outMin), outMax);
  };

  // Start/stop camera stream when selectedCamera or isTorchOn/zoom changes
  const startCamera = async () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clear video element srcObject
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (!selectedCamera) return;

    // Build dynamic constraints based on last known capabilities
    const dynamicConstraints: MediaTrackConstraints = {
      deviceId: { exact: selectedCamera },
      facingMode: { ideal: 'environment' },
      width: { ideal: width },
      height: { ideal: height },
      aspectRatio: { ideal: width / height },
    };

    const constraints: MediaStreamConstraints = { video: dynamicConstraints };

    // Log the applied configuration
    onLog?.(`Applying video constraints: ${JSON.stringify(constraints, null, 2)}`);

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        onReady?.();
      }
      onLog?.(`Camera stream started: ${selectedCamera}`);

    } catch (e: unknown) {
      onLog?.(`getUserMedia error: ${(e as Error).message}`);

      // Clean up on error
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        onLog?.('Camera stream stopped');
      }
    };
    // eslint-disable-next-line
  }, [selectedCamera,]);

  // Frame capture loop
  useEffect(() => {
    let timeoutId: number;

    function scanFrame() {
      if (paused) return;

      const video = videoRef.current!;
      const canvas = canvasRef.current!;

      if (video.readyState === 4) {
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth;
        }

        if (canvas.height !== video.videoHeight) {
          canvas.height = video.videoHeight;
        }

        const ctx = canvas.getContext('2d');

        if (ctx) {
          ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

          //code to crop to the box, but can be misaligned causing issues.
          /*const roiWidth = 220;
          const roiHeight = 120;
          const x = (video.videoWidth - roiWidth) / 2;
          const y = (video.videoHeight - roiHeight) / 2;

          const imageData = ctx.getImageData(x, y, roiWidth, roiHeight);*/

          const imageData = ctx.getImageData(0, 0, video.videoWidth, video.videoHeight);

          onFrame(imageData); // Pass original or processed as needed
        }
      }
      timeoutId = window.setTimeout(scanFrame, 100); // Process every 200ms
    }

    if (videoRef.current && !paused) {
      scanFrame();
    }

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line
  }, [
    selectedCamera, onFrame, paused,
  ]);

  return (
    <>
      <div style={{ position: 'relative', maxWidth: '100%', width: '100%', height: '100%', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#222' }}
          playsInline
          muted
          autoPlay
        />
        {/* Scanning box overlay */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 340,
            height: 200,
            border: '3px dashed #0f0',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>
      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef}
        style={{ display: 'none' }} />
    </>
  );
};

